import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  departVerify,
  departOrder,
  arriveOrder,
  startService,
  submitPunchCard,
  submitOrderFinish,
} from "@/api/staff";
import { uploadImage } from "@/api/upload";
import { requestLocationOnUserAction } from "@/utils/location";
import type { LocationResult } from "@/utils/location";
import { toastSuccess, toastWarning } from "@/utils/toast";

/** 打卡模式配置 */
const PUNCH_CONFIG = {
  depart: {
    title: "出发打卡",
    subtitlePrefix: "出发",
    placeholder: "出发备注...",
    btnText: "确认出发",
    successMsg: "已确认出发",
  },
  arrive: {
    title: "到达打卡",
    subtitlePrefix: "到达",
    placeholder: "到达备注...",
    btnText: "确认到达",
    successMsg: "已确认到达",
  },
  start: {
    title: "开始服务打卡",
    subtitlePrefix: "开始服务",
    placeholder: "服务开始备注...",
    btnText: "确认开始服务",
    successMsg: "已确认开始服务",
  },
  finish: {
    title: "完成服务打卡",
    subtitlePrefix: "完成服务",
    placeholder: "服务完成备注...",
    btnText: "确认完成服务",
    successMsg: "已完成服务",
  },
} as const;

type PunchType = keyof typeof PUNCH_CONFIG;

export default function PunchDepartPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = Number(searchParams.get("id") || 0);
  // 打卡类型：depart / arrive / start / finish
  const punchType = (searchParams.get("type") as PunchType) || "depart";
  const config = PUNCH_CONFIG[punchType];

  const [locating, setLocating] = useState(true);
  const [location, setLocation] = useState<LocationResult | null>(null);
  const [remark, setRemark] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // 获取定位
  useEffect(() => {
    (async () => {
      setLocating(true);
      try {
        const res = await requestLocationOnUserAction();
        if (res.success && res.location) {
          setLocation(res.location);
        } else {
          toastWarning(res.error || "定位失败");
        }
      } catch (e) {
        toastWarning("定位失败，请重试");
      } finally {
        setLocating(false);
      }
    })();
  }, []);

  const handleFilePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    if (images.length >= 4) return toastWarning("最多上传4张图片");
    const picked = Array.from(files).slice(0, 4 - images.length);
    try {
      const uploaded = await Promise.all(picked.map((f) => uploadImage(f)));
      setImages((prev) => [...prev, ...uploaded].slice(0, 4));
    } catch {}
    e.target.value = "";
  };

  const removeImage = (idx: number) => setImages((prev) => prev.filter((_, i) => i !== idx));

  // 构建位置参数
  const locParams = location ? {
    lat: location.latitude,
    lng: location.longitude,
    address: location.formattedAddress || location.address || "",
  } : undefined;

  const handleSubmit = async () => {
    if (!orderId) return toastWarning("缺少订单ID");
    if (!location && !locating) return toastWarning("请先获取位置信息");
    if (submitting) return;
    setSubmitting(true);

    try {
      switch (punchType) {
        case "depart": {
          // 出发：核验 + 确认出发（携带经纬度+地址）
          await departVerify(orderId);
          await departOrder(orderId, locParams);
          break;
        }
        case "arrive": {
          // 到达：确认到达（携带经纬度+地址）
          await arriveOrder(orderId, locParams);
          break;
        }
        case "start": {
          // 开始服务（携带经纬度+地址）
          await startService(orderId, locParams);
          break;
        }
        case "finish": {
          // 完成服务：提交打卡并完成（携带经纬度+地址+图片上传）
          const finishData = {
            order_id: orderId,
            content: remark.trim(),
            imgs: images,
            ...locParams,
          };
          await submitOrderFinish(finishData);
          toastSuccess(config.successMsg);
          navigate(`/workspace/order/detail?id=${orderId}`, { replace: true });
          return; // 提前返回，不再走下面的通用 submitPunchCard
        }
      }

      // depart / arrive / start 模式：如果有图片或备注，额外提交打卡记录（携带经纬度+地址）
      if (images.length > 0 || remark.trim()) {
        await submitPunchCard({
          order_id: orderId,
          content: remark.trim(),
          imgs: images,
          ...locParams,
        });
      }

      toastSuccess(config.successMsg);
      navigate(`/workspace/order/detail?id=${orderId}`, { replace: true });
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* 头部 */}
      <div className="bg-[#2C2C3A] pb-5 px-5"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
        <div className="flex items-center gap-3 mb-1">
          <button onClick={() => navigate(-1)} className="w-9 h-9 flex items-center justify-center rounded-full bg-white/12 border border-white/20 cursor-pointer active:scale-95">
            <i className="ri-arrow-left-line text-white text-lg" />
          </button>
          <span className="text-base font-bold text-white">{config.title}</span>
        </div>
        <p className="text-xs text-white/50 mt-1">请确认当前位置后拍照{config.subtitlePrefix}打卡</p>
      </div>

      {/* 地图区域 */}
      <div className="mx-4 -mt-3 rounded-2xl overflow-hidden shadow-md relative" style={{ height: "240px", background: "#E8E8E8" }}>
        {location ? (
          <>
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-green-50">
              <div className="text-center">
                <i className="ri-map-pin-fill text-4xl text-[#FE7A3A] mb-2 block" />
                <p className="text-sm font-medium text-gray-700">当前位置</p>
                <p className="text-xs text-gray-500 mt-1 max-w-[200px] truncate mx-auto">
                  {location.formattedAddress || location.address || `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`}
                </p>
              </div>
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="w-10 h-10 rounded-full bg-[#FE7A3A]/90 flex items-center justify-center shadow-lg animate-bounce">
                <i className="ri-navigation-fill text-white text-sm" />
              </div>
            </div>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {locating ? (
              <div className="flex flex-col items-center gap-2">
                <i className="ri-loader-4-line text-2xl text-[#FE7A3A] animate-spin" />
                <span className="text-xs text-gray-400">正在获取位置...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <i className="ri-map-pin-line text-2xl text-gray-300" />
                <span className="text-xs text-gray-400">定位失败</span>
                <button onClick={() => window.location.reload()} className="text-xs text-[#FE7A3A] mt-1">重新定位</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 地址展示 */}
      {location && (
        <div className="mx-4 mt-3 bg-white rounded-xl p-3.5 shadow-sm">
          <div className="flex items-start gap-2.5">
            <i className="ri-map-pin-2-fill text-[#FE7A3A] text-base shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-800 leading-relaxed">{location.formattedAddress || location.address || "-"}</p>
              <p className="text-xs text-gray-400 mt-1">
                经纬度：{location.latitude?.toFixed(6)}, {location.longitude?.toFixed(6)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 备注输入 */}
      <div className="mx-4 mt-3 bg-white rounded-xl p-4 shadow-sm">
        <label className="text-sm font-medium text-gray-800 mb-2 block">备注（选填）</label>
        <textarea
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder={`请输入${config.placeholder}`}
          className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none outline-none focus:border-[#FE7A3A]"
          rows={3}
        />
      </div>

      {/* 图片上传 — 仅服务开始和完成服务时显示 */}
      {(punchType === "start" || punchType === "finish") && (
      <div className="mx-4 mt-3 bg-white rounded-xl p-4 shadow-sm">
        <label className="text-sm font-medium text-gray-800 mb-2.5 block">
          拍照上传 <span className="text-xs text-gray-400 font-normal ml-1">（选填）</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {images.map((u, idx) => (
            <div key={idx} className="relative w-[72px] h-[72px]">
              <img src={u} alt="" className="w-full h-full rounded-xl object-cover" />
              <button
                type="button"
                onClick={() => removeImage(idx)}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black/50 flex items-center justify-center"
              >
                <i className="ri-close-line text-white text-xs" />
              </button>
            </div>
          ))}
          {images.length < 4 && (
            <label className="w-[72px] h-[72px] rounded-xl bg-gray-50 border border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:bg-gray-100 active:scale-[0.98]">
              <i className="ri-camera-line text-xl text-gray-400" />
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={handleFilePick} />
            </label>
          )}
        </div>
        <p className="text-xs text-gray-300 mt-2">最多上传4张图片</p>
      </div>
      )}

      {/* 底部留白 + 提交按钮 */}
      <div className="mt-auto px-4 pb-8 pt-4">
        <button
          onClick={handleSubmit}
          disabled={!location || submitting}
          className={`w-full py-3.5 rounded-2xl text-base font-semibold transition-all ${
            location && !submitting
              ? "bg-[#FE7A3A] text-white active:scale-[0.98] shadow-lg shadow-[#FE7A3A]/30"
              : "bg-gray-200 text-gray-400"
          }`}
        >
          {submitting ? "提交中..." : config.btnText}
        </button>
      </div>
    </div>
  );
}
